/**
 * PostProcessingStack (VQ-001 / AD-194 §2): owns the EffectComposer lifecycle so
 * Game.ts delegates its render call to ONE place — which is exactly where the
 * tier-based degradation path forks without scattered conditionals:
 *
 * - full profile → RenderPass → SSAOPass → UnrealBloomPass → OutputPass. The
 *   composer renders the scene into an HDR (half-float) target, bloom operates
 *   on the linear HDR image, and the OutputPass applies the renderer's tone
 *   mapping (AgX) + sRGB conversion when writing to screen.
 * - degraded profile (mobile / `?quality=low`, passPlan() = []) → NO composer is
 *   allocated at all; render() draws directly. AgX tone mapping still applies
 *   (renderer.toneMapping does the same job on the direct path — AD-194 §3
 *   "AgX retained").
 *
 * The stack also centralises the renderer-level presentation config the profile
 * drives (tone mapping + shadow-map enable/type). WGP-001b retains this concrete
 * WebGL stack verbatim and gives WebGPU its node-based sibling behind PostFxLike;
 * both consume the same passPlan()/QualityProfile decision core.
 *
 * Like builder.ts/threeLoader.ts this is browser glue exercised by the build,
 * not by `node --test` — every DECISION (which passes exist, in what order, per
 * profile) lives in the pure, unit-tested passPlan()/resolveQualityProfile()
 * (quality.ts). Presentation-only, off the per-tick path: no sim, no wire, no
 * collision state (AD-026/AD-031 untouched).
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import type { Pass } from 'three/examples/jsm/postprocessing/Pass.js';

import { adoptFrameShadowPolicy, armFrameShadows } from './frameShadows.js';
import { BLOOM_DEFAULTS, passPlan, type QualityProfile } from './quality.js';

/**
 * Whether an object stays OUT of the SSAO normal/depth pass (q-demo-vfx-pass). A mesh whose
 * material writes no depth (additive VFX quads, telegraph fills, the mirror-image body) is a
 * see-through surface: drawn into the normal pass it occludes as if solid, and the AO then
 * darkens a quad-shaped patch behind it (measured: a dark square behind the level-up glow,
 * desktop lane run 20261024T125423Z). Three's SSAOPass already leaves points and lines out;
 * this extends the same rule to depth-less meshes.
 */
export function excludedFromAo(object: THREE.Object3D): boolean {
  const mesh = object as THREE.Mesh;
  if (!(mesh as { isMesh?: boolean }).isMesh || !mesh.visible) return false;
  const m = mesh.material;
  if (m === undefined) return false;
  return Array.isArray(m) ? m.length > 0 && m.every((x) => !x.depthWrite) : !m.depthWrite;
}

/** Extend an SSAOPass's visibility override with {@link excludedFromAo}. */
function keepDepthlessOutOfAo(ssao: SSAOPass, scene: THREE.Scene): void {
  const pass = ssao as unknown as {
    _overrideVisibility?: () => void;
    _visibilityCache?: THREE.Object3D[];
  };
  const base = pass._overrideVisibility?.bind(ssao);
  const cache = pass._visibilityCache;
  if (base === undefined || !Array.isArray(cache)) return; // a three without the hook: unchanged
  pass._overrideVisibility = () => {
    base();
    scene.traverse((object) => {
      if (excludedFromAo(object)) {
        object.visible = false;
        cache.push(object);
      }
    });
  };
}

/**
 * The render delegate shared by the WebGL2 EffectComposer and WebGPU node
 * pipelines (WGP-001b). Game owns only this lifecycle surface.
 */
export interface PostFxLike {
  readonly composed: boolean;
  render(scene: THREE.Scene, camera: THREE.Camera, dtMs: number): void;
  /** Last frame's draw calls per pass (diagnostics; the WebGL stack only). */
  passCalls?(): Record<string, number>;
  setSize(width: number, height: number): void;
  dispose(): void;
}

export class PostProcessingStack implements PostFxLike {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly profile: QualityProfile;
  /** Null on the degraded path (passPlan() = []) — render() draws directly. */
  private composer: EffectComposer | null = null;
  private renderPass: RenderPass | null = null;
  private ssaoPass: SSAOPass | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private outputPass: OutputPass | null = null;

  constructor(
    renderer: THREE.WebGLRenderer,
    profile: QualityProfile,
    scene: THREE.Scene,
    camera: THREE.Camera,
  ) {
    this.renderer = renderer;
    this.profile = profile;

    // Renderer-level presentation config the profile drives (AD-194 §1/§3):
    // AgX tone mapping on EVERY profile (the OutputPass reads it on the composer
    // path; the renderer applies it directly on the degraded path), and the
    // tier-controlled shadow map.
    renderer.toneMapping = THREE.AgXToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = profile.shadows.enabled;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const plan = passPlan(profile);
    // H-1145: the composer renders the scene twice a frame (RenderPass + SSAO's
    // normal pass); the shadow map is redrawn once, not per render (frameShadows.ts).
    adoptFrameShadowPolicy(renderer.shadowMap, plan.length > 0);
    this.measureShadowCalls(renderer);
    if (plan.length === 0) return; // degraded path: no composer at all

    const size = renderer.getSize(new THREE.Vector2());
    const composer = new EffectComposer(renderer);
    composer.setPixelRatio(renderer.getPixelRatio());
    for (const pass of plan) {
      if (pass === 'render') {
        this.renderPass = new RenderPass(scene, camera);
        composer.addPass(this.renderPass);
      } else if (pass === 'ssao') {
        // Low-cost AO (AD-194 §2b): a small kernel radius keeps the effect a
        // grounding contact shade rather than a heavy screen-space GI attempt.
        const ssao = new SSAOPass(scene, camera, size.x, size.y);
        ssao.kernelRadius = 6;
        ssao.minDistance = 0.005;
        ssao.maxDistance = 0.1;
        keepDepthlessOutOfAo(ssao, scene);
        this.ssaoPass = ssao;
        composer.addPass(ssao);
      } else if (pass === 'bloom') {
        const params = this.profile.bloom ?? BLOOM_DEFAULTS;
        this.bloomPass = new UnrealBloomPass(
          new THREE.Vector2(size.x, size.y),
          params.strength,
          params.radius,
          params.threshold,
        );
        composer.addPass(this.bloomPass);
      } else {
        // 'output': tone mapping (renderer.toneMapping = AgX) + sRGB to screen.
        this.outputPass = new OutputPass();
        composer.addPass(this.outputPass);
      }
      this.measurePass(renderer, pass, composer.passes[composer.passes.length - 1]);
    }
    this.composer = composer;
  }

  /**
   * Draw calls each pass submitted in the last frame (`shadow` split out of the
   * scene renders that triggered it) — the probe's per-pass breakdown, read by
   * the DEV scene dump. Two counter reads per pass per frame.
   */
  passCalls(): Record<string, number> {
    return { ...this.lastPassCalls };
  }

  private readonly lastPassCalls: Record<string, number> = {};

  private measurePass(renderer: THREE.WebGLRenderer, name: string, pass: Pass | undefined): void {
    if (pass === undefined) return;
    const original = pass.render.bind(pass);
    pass.render = (...args: Parameters<Pass['render']>): void => {
      const before = renderer.info.render.calls;
      const shadowBefore = this.lastPassCalls.shadow ?? 0;
      original(...args);
      const shadowDuring = (this.lastPassCalls.shadow ?? 0) - shadowBefore;
      this.lastPassCalls[name] = renderer.info.render.calls - before - shadowDuring;
    };
  }

  /** The unwrapped `shadowMap.render`, restored on dispose so stacks never nest wrappers. */
  private restoreShadowRender: (() => void) | null = null;

  private measureShadowCalls(renderer: THREE.WebGLRenderer): void {
    const shadowMap = renderer.shadowMap as unknown as { render: (...args: unknown[]) => void };
    const original = shadowMap.render;
    shadowMap.render = (...args: unknown[]): void => {
      const before = renderer.info.render.calls;
      original.apply(shadowMap, args);
      this.lastPassCalls.shadow =
        (this.lastPassCalls.shadow ?? 0) + renderer.info.render.calls - before;
    };
    this.restoreShadowRender = () => {
      shadowMap.render = original;
    };
  }

  /** Whether the composer path is active (diagnostics; false ⇒ direct render). */
  get composed(): boolean {
    return this.composer !== null;
  }

  /**
   * The one render call Game.ts delegates to (AD-194 §2). Keeps the pass
   * scene/camera refs current (cheap assignments) so the stack never renders a
   * stale scene if the caller ever rebuilds one.
   */
  render(scene: THREE.Scene, camera: THREE.Camera, dtMs: number): void {
    this.lastPassCalls.shadow = 0;
    armFrameShadows(this.renderer.shadowMap);
    if (this.composer === null) {
      this.renderer.render(scene, camera);
      return;
    }
    if (this.renderPass !== null) {
      this.renderPass.scene = scene;
      this.renderPass.camera = camera;
    }
    if (this.ssaoPass !== null) {
      this.ssaoPass.scene = scene;
      this.ssaoPass.camera = camera as THREE.PerspectiveCamera;
    }
    this.composer.render(dtMs / 1000);
  }

  /** Track the renderer's drawing-buffer size (Game.onResize calls this after setSize). */
  setSize(width: number, height: number): void {
    this.composer?.setSize(width, height);
  }

  /** Free the composer's render targets + per-pass GPU resources. */
  dispose(): void {
    this.restoreShadowRender?.();
    this.restoreShadowRender = null;
    this.ssaoPass?.dispose();
    this.bloomPass?.dispose();
    this.outputPass?.dispose();
    this.composer?.dispose();
    this.composer = null;
    this.renderPass = null;
    this.ssaoPass = null;
    this.bloomPass = null;
    this.outputPass = null;
  }
}
